import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setBuildingState } from "../../store/actions/permitFormActions/buildingStateActions";
import {
  bondingLabel,
  feeSimpleLabel,
  mortgageLabel,
  termiteLabel,
  projectLabel,
} from "../../data/buildingPermit/fieldDatas";
import { formikRef } from "../../store/formikStore/formikStore";
import { LoadingButton } from "@mui/lab";
import {
  handleSaveProjects,
  pushNewDynamicSavedData,
} from "../../store/actions/dataActions";
import {
  TEMPLATE_CATEGORY_IDS,
  getTemplateCategoryConfig,
  buildTemplateFieldValues,
} from "../permitForms/utils/templateCategoryHelpers";

const TEMPLATE_CATEGORY_BY_TYPE = {
  feeSimple: TEMPLATE_CATEGORY_IDS.FEE_SIMPLE,
  bonding: TEMPLATE_CATEGORY_IDS.BONDING,
  mortgage: TEMPLATE_CATEGORY_IDS.MORTGAGE,
};

const FIXED_CATEGORY_FIELDS = {
  feeSimple: [
    {
      name: "fee_simple_name",
      field_id: "84de4018-b9cf-4fd0-a737-bba2d513ef5b",
    },
    {
      name: "fee_simple_address",
      field_id: "01419daf-8d1c-403a-9b7d-8b55210dd68f",
    },
    {
      name: "fee_simple_street",
      field_id: "ec8c5fdf-f31d-4a68-b76b-ad120f8e976b",
    },
    {
      name: "fee_simple_city",
      field_id: "01333ed8-4652-49d5-bd95-eadb60602492",
    },
    {
      name: "fee_simple_state",
      field_id: "c79b39c7-5d57-430a-987d-d9be311c4c1e",
    },
    {
      name: "fee_simple_zip",
      field_id: "d847bd77-d8e2-4fba-b43e-a43c95e864ce",
    },
    {
      name: "fee_simple_phone",
      field_id: "be101939-7c85-41f1-a693-4b383e8d4085",
    },
    {
      name: "fee_simple_email",
      field_id: "3056bff1-b3c0-4577-bb87-0f537edfea4c",
    },
  ],
  bonding: [
    { name: "bonding_name", field_id: "0b78370f-2fdc-46da-817c-5435dbd01782" },
    {
      name: "bonding_address",
      field_id: "212e07b7-2187-439d-9a8a-a50298c53270",
    },
    {
      name: "bonding_street",
      field_id: "b8e18c44-1b87-4548-8b34-6a14e2bef640",
    },
    { name: "bonding_city", field_id: "8e4fde63-36d5-447b-a948-054078ec691a" },
    { name: "bonding_state", field_id: "be8c20be-91df-4092-ada4-834792ce82b1" },
    { name: "bonding_zip", field_id: "9af49b26-7e64-4f90-827d-039f0991db98" },
    { name: "bonding_phone", field_id: "9cca0802-6df3-4557-b913-daa54078cfc7" },
  ],
  mortgage: [
    { name: "mortgage_name", field_id: "1bca889b-286d-4890-8b5a-ec7dd0dd561c" },
    {
      name: "mortgage_address",
      field_id: "6ccc487a-3463-44b4-88d0-efcd04794ee1",
    },
    {
      name: "mortgage_street",
      field_id: "4e38bce7-553c-426e-9867-6a6a28b8c235",
    },
    { name: "mortgage_city", field_id: "10df671b-40fd-46d1-a28e-4d125c9c7beb" },
    {
      name: "mortgage_state",
      field_id: "c40e54d0-f984-4c38-a0c3-f0d4e2c12265",
    },
    { name: "mortgage_zip", field_id: "06c3ce2e-00c6-4692-a570-606277ca6f04" },
    {
      name: "mortgage_phone",
      field_id: "8353c8fe-9123-4fc6-8078-1eaa99681de4",
    },
    {
      name: "mortgage_email",
      field_id: "931aca86-4729-4d91-a61f-90a24a52c635",
    },
  ],
};

const getFieldName = (field) => field?.name || field?.field_name;

const getFieldLabel = (field, fallbackLabels) => {
  const name = getFieldName(field);
  return (
    fallbackLabels?.[name] ||
    String(name || "")
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase())
  );
};

const buildEmptyValuesForFields = (fields = []) =>
  fields.reduce(
    (values, field) => ({ ...values, [getFieldName(field)]: "" }),
    {},
  );

const FinancialBPModal = ({
  type,
  open,
  title,
  handleStateNo,
  fieldsToCheck,
  onTemplateAdded,
}) => {
  const formik = formikRef.current;
  const dispatch = useDispatch();

  const {
    newProjectLoading,
    feeSimpleLoading,
    bondingLoading,
    mortgageLoading,
    termiteLoading,
    activeTemplateCategory,
  } = useSelector((state) => state.buildingState);

  const [submitDisabled, setSubmitDisabled] = useState(true);

  const [fieldValues, setFieldValues] = useState({
    project: {
      project_contact: "",
      project_c_phone: "",
      loading: newProjectLoading,
      formikName: "projectContact",
      savedProjectName: "projectContact",
      label: projectLabel,
    },
    feeSimple: {
      ...buildEmptyValuesForFields(FIXED_CATEGORY_FIELDS.feeSimple),
      loading: feeSimpleLoading,
      formikName: "feeSimpleTitleholder",
      savedProjectName: "feeSimple",
      label: feeSimpleLabel,
    },
    bonding: {
      ...buildEmptyValuesForFields(FIXED_CATEGORY_FIELDS.bonding),
      loading: bondingLoading,
      formikName: "bondingCompany",
      savedProjectName: "bonding",
      label: bondingLabel,
    },
    mortgage: {
      ...buildEmptyValuesForFields(FIXED_CATEGORY_FIELDS.mortgage),
      loading: mortgageLoading,
      formikName: "mortgageLender",
      savedProjectName: "mortgageLender",
      label: mortgageLabel,
    },
    termite: {
      protection_by: "",
      treatment_method: "",
      loading: termiteLoading,
      formikName: "termite",
      savedProjectName: "termite",
      label: termiteLabel,
    },
  });

  // Categories with a fixed field list (feeSimple/bonding/mortgage) always use
  // it. Project/Termite have no template category, so they keep using
  // whatever fieldsToCheck was passed in — unaffected by this change.
  const resolvedFields = useMemo(() => {
    const fixedFields = FIXED_CATEGORY_FIELDS[type];
    if (fixedFields) return fixedFields;

    return (fieldsToCheck || []).map((name) => ({ name }));
  }, [type, fieldsToCheck]);

  const resolvedFieldNames = useMemo(
    () => resolvedFields.map(getFieldName).filter(Boolean),
    [resolvedFields],
  );

  // Only the "name" and phone/email-style required fields need validation;
  // for fixed categories this is just the resolved field names themselves.
  const validationFieldNames = useMemo(
    () =>
      FIXED_CATEGORY_FIELDS[type] ? resolvedFieldNames : fieldsToCheck || [],
    [type, resolvedFieldNames, fieldsToCheck],
  );

  useEffect(() => {
    if (!open || !resolvedFieldNames.length) return;

    setFieldValues((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        ...resolvedFieldNames.reduce(
          (values, name) => ({
            ...values,
            [name]: prev[type]?.[name] ?? "",
          }),
          {},
        ),
      },
    }));
  }, [open, resolvedFieldNames, type]);

  useEffect(() => {
    const callFunc = () => {
      let error = false;

      validationFieldNames.forEach((item) => {
        const val = String(fieldValues[type]?.[item] ?? "");

        if (item === "project_c_phone") {
          if (val === "" || val.length < 12) {
            error = true;
          }
        } else if (item === "treatment_method") {
          // treatment_method is a select field, just check if it's empty
          if (val === "") {
            error = true;
          }
        } else if (
          item.endsWith("_name") ||
          item === "protection_by" ||
          item === "project_contact"
        ) {
          // Only the primary "name" style field is required to have real
          // content; other fields (phone/email/address parts) are optional.
          if (val === "" || val.length < 3) {
            error = true;
          }
        }
      });

      setSubmitDisabled(error);
    };

    callFunc();
  }, [fieldValues, validationFieldNames, type]);

  const runFieldCange = (event) => {
    const { name, value } = event.target;

    setFieldValues((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [name]: value,
      },
    }));
  };

  const handleAddNew = async () => {
    try {
      await dispatch(setBuildingState({ [fieldValues[type].loading]: true }));

      const newID = Date.now().toString();
      const createdAt = Math.floor(Date.now() / 1000);
      const reduxKey = fieldValues[type].savedProjectName;

      const categoryFieldValues = resolvedFieldNames.reduce(
        (values, name) => ({
          ...values,
          [name]: fieldValues[type][name] ?? "",
        }),
        {},
      );

      const templateCategoryId = TEMPLATE_CATEGORY_BY_TYPE[type];
      const fieldValuesById = FIXED_CATEGORY_FIELDS[type]
        ? FIXED_CATEGORY_FIELDS[type].reduce(
            (values, field) => ({
              ...values,
              [field.field_id]: fieldValues[type][field.name] ?? "",
            }),
            {},
          )
        : {};

      const savedProjectObj = {
        id: newID,
        ...categoryFieldValues,
        ...(templateCategoryId && Object.keys(fieldValuesById).length
          ? {
              category_id: templateCategoryId,
              field_values: fieldValuesById,
            }
          : {}),
        created_at: createdAt,
      };

      await dispatch(
        pushNewDynamicSavedData({ key: reduxKey, val: savedProjectObj }),
      );

      await dispatch(handleSaveProjects());

      const config = getTemplateCategoryConfig(templateCategoryId);
      const realFields = activeTemplateCategory?.fields?.length
        ? activeTemplateCategory.fields
        : resolvedFields;
      const formikPatchValues = config
        ? buildTemplateFieldValues(savedProjectObj, realFields, config)
        : {};

      formik?.setValues((prevValues) => ({
        ...prevValues,
        ...categoryFieldValues,
        ...formikPatchValues,
        [fieldValues[type].formikName]: newID,
      }));

      dispatch(setBuildingState({ activeTemplateCategory: null }));
      handleStateNo();
    } catch (e) {
    } finally {
      await dispatch(setBuildingState({ [fieldValues[type].loading]: false }));
    }
  };

  const cleanPhone = (e) => {
    const { name, value } = e.target;

    let digitsOnly = value.replace(/\D/g, ""); // Remove non-numeric characters

    // If the number of digits is greater than 10, slice to 10 digits
    if (digitsOnly?.length > 10) {
      digitsOnly = digitsOnly.slice(0, 10);
    }

    let formattedPhoneNumber = "";

    // Add dashes after 3rd and 6th digits
    for (let i = 0; i < digitsOnly.length; i++) {
      if (i === 3 || i === 6) {
        formattedPhoneNumber += "-";
      }
      formattedPhoneNumber += digitsOnly[i];
    }

    setFieldValues((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [name]: formattedPhoneNumber,
      },
    }));
  };

  return (
    <Modal
      open={open}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "transparent",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%", // Adjust width for smaller screens
          maxWidth: 600, // Set a maximum width
          bgcolor: "background.paper",
          boxShadow: "1px 10px 10px 5px rgba(0, 0, 0, 0.5)",
          borderRadius: "8px",
        }}
      >
        {/* Title Section */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            p: 2,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h5" align="center" color="primary">
            {title}
          </Typography>
        </Box>

        {/* Scrollable Content Section */}
        <Box
          sx={{
            maxHeight: "60vh", // Limit height to 60% of the viewport
            overflowY: "auto", // Enable scrolling when content overflows
            p: 2,
          }}
        >
          <>
            {resolvedFields.map((fieldConfig, i) => {
              const field = getFieldName(fieldConfig);
              const label = getFieldLabel(fieldConfig, fieldValues[type].label);

              if (field === "treatment_method") {
                return (
                  <FormControl
                    key={field + i}
                    fullWidth
                    margin="normal"
                    variant="filled"
                  >
                    <InputLabel>{label}</InputLabel>
                    <Select
                      name={field}
                      value={fieldValues[type][field] ?? ""}
                      onChange={runFieldCange}
                      onBlur={(event) => {
                        formik?.handleBlur(event);
                        formik?.setFieldTouched(field, true, true);
                      }}
                      error={
                        formik?.touched[field] && Boolean(formik?.errors[field])
                      }
                      MenuProps={{
                        PaperProps: {
                          style: {
                            marginTop: "1.5px",
                            maxHeight: 200,
                            width: "auto",
                            maxWidth: "100%",
                          },
                        },
                      }}
                    >
                      <MenuItem value="treatment_soil">Soil</MenuItem>
                      <MenuItem value="treatment_bait">Bait</MenuItem>
                      <MenuItem value="treatment_wood">Wood</MenuItem>
                    </Select>
                  </FormControl>
                );
              }
              return (
                <TextField
                  autoComplete="off"
                  key={field + i}
                  fullWidth
                  onKeyUp={(e) => {
                    if (field === "project_c_phone") {
                      cleanPhone(e);
                    }
                  }}
                  margin="normal"
                  variant="filled"
                  label={label}
                  name={field}
                  value={fieldValues[type][field] ?? ""}
                  onChange={runFieldCange}
                  onBlur={(event) => {
                    formik?.handleBlur(event);
                    formik?.setFieldTouched(field, true, true);
                  }}
                  error={
                    formik?.touched[field] && Boolean(formik?.errors[field])
                  }
                  helperText={formik?.touched[field] && formik?.errors[field]}
                />
              );
            })}
          </>
        </Box>

        {/* Footer Section with Buttons */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            p: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Button
            fullWidth
            onClick={() => {
              dispatch(setBuildingState({ activeTemplateCategory: null }));
              handleStateNo();
            }}
            color="error"
            variant="contained"
          >
            Cancel
          </Button>
          <LoadingButton
            loading={fieldValues[type].loading}
            disabled={submitDisabled}
            fullWidth
            onClick={handleAddNew}
            color="success"
            variant="contained"
          >
            Add
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default React.memo(FinancialBPModal);
